import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';

export const PendingInvoicesAlert = ({ pendingCount, pendingInvoices = [] }) => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        // Show alert if there are pending invoices and not dismissed
        if (pendingCount > 0 && !isDismissed) {
            // Small delay for better UX
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [pendingCount, isDismissed]);

    // Auto-hide after 15 seconds
    useEffect(() => {
        if (isVisible) {
            const timer = setTimeout(() => {
                setIsVisible(false);
            }, 15000);
            return () => clearTimeout(timer);
        }
    }, [isVisible]);

    const handleDismiss = () => {
        setIsVisible(false);
        setIsDismissed(true);
        // Reset dismissed state after 5 minutes so it shows again
        setTimeout(() => setIsDismissed(false), 5 * 60 * 1000);
    };

    const handleGoToInvoices = () => {
        setIsVisible(false);
        navigate('/dashboard/invoices');
    };

    if (!isVisible || pendingCount === 0) return null;

    return (
        <div 
            className="fixed bottom-4 right-4 z-50 max-w-md animate-in slide-in-from-right-5 duration-300"
            data-testid="pending-invoices-alert"
        >
            <div className="bg-amber-50 border-2 border-amber-400 rounded-xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-amber-400 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-900" />
                        <span className="font-bold text-amber-900">
                            ¡Atención! Facturas Pendientes
                        </span>
                    </div>
                    <button 
                        onClick={handleDismiss}
                        className="text-amber-900 hover:text-amber-700 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                
                {/* Content */}
                <div className="p-4">
                    <p className="text-amber-800 mb-3">
                        Tienes <span className="font-bold text-lg">{pendingCount}</span> factura{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''} de verificación.
                    </p>
                    
                    {/* Show first 3 pending invoices */}
                    {pendingInvoices.length > 0 && (
                        <div className="mb-3 space-y-1">
                            {pendingInvoices.slice(0, 3).map((inv, idx) => (
                                <div key={idx} className="text-sm bg-amber-100 rounded px-2 py-1 flex justify-between">
                                    <span className="font-medium text-amber-900 truncate max-w-[180px]">
                                        {inv.name}
                                    </span>
                                    <span className="text-amber-700 font-semibold">
                                        ${inv.amount?.toFixed(2)}
                                    </span>
                                </div>
                            ))}
                            {pendingInvoices.length > 3 && (
                                <p className="text-xs text-amber-600 text-center">
                                    y {pendingInvoices.length - 3} más...
                                </p>
                            )}
                        </div>
                    )}
                    
                    {/* Instructions */}
                    <div className="bg-white rounded-lg p-3 border border-amber-200">
                        <p className="text-amber-900 font-semibold mb-2 flex items-center gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Para resolver:
                        </p>
                        <ol className="text-amber-800 text-sm space-y-1 list-decimal list-inside">
                            <li><strong>Verifica</strong> que el nombre y apellido coincidan con el pago</li>
                            <li><strong>Borra</strong> la factura pendiente</li>
                            <li><strong>Sube de nuevo</strong> con los datos correctos</li>
                        </ol>
                    </div>
                </div>
                
                {/* Footer */}
                <div className="px-4 pb-3 flex gap-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleDismiss}
                        className="flex-1 border-amber-400 text-amber-700 hover:bg-amber-100"
                    >
                        Entendido
                    </Button>
                    <Button 
                        size="sm" 
                        onClick={handleGoToInvoices}
                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                        data-testid="go-to-invoices-btn"
                    >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Ver Facturas
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PendingInvoicesAlert;
